import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { useTemporaryStorageContext } from '#/containers/TemporaryStorageContext';
import { useUserEnvironment } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { CmsApiSubscribe } from '#/modules/cms/api/types/CmsApiSubscribe';
import { RichError } from '#/utils/RichError';
import { postJson } from '#/utils/http/postJson';
import { trackError } from '#/utils/trackError';
import { isUrlWithProtocol } from '#/utils/url/isUrlWithProtocol';
import { showModal } from '#/zustand-stores/modalStore';

type Props = {
  blockId: string;
  index: number;
  redirectUrl?: string;
};

export function useCmsSubscribe({ blockId, index, redirectUrl }: Props) {
  const { i18n } = useI18n();
  const navigate = useNavigate();
  const { user } = useUserEnvironment();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { temporaryStorage } = useTemporaryStorageContext();

  const subscribe = useCallback(() => {
    const showEmailSignupModal = (cmsSubscribe: CmsApiSubscribe) => {
      showModal('EMAIL_SIGNUP', {
        title: i18n('Create an account on Idealist in order to subscribe'),
        cmsSubscribe,
        onLogin: () => {
          const postBaseUrl = '/data/website/button-link-campaign-signup/';
          postJson(postBaseUrl + blockId, {
            index,
          });

          if (typeof redirectUrl === 'string') {
            if (isUrlWithProtocol(redirectUrl)) {
              window.location.href = redirectUrl;
              return;
            }

            navigate(
              redirectUrl.startsWith('/') ? redirectUrl : `/${redirectUrl}`,
            );
          } else {
            setIsSubscribed(true);
          }
        },
      });
    };

    const loggedInVerifiedWithInfo = Boolean(
      user &&
        user.email !== null &&
        user.emailVerified &&
        user.firstName &&
        user.lastName,
    );

    if (loggedInVerifiedWithInfo) {
      const postBaseUrl = '/data/website/button-link-campaign-signup/';
      postJson(postBaseUrl + blockId, {
        index,
      })
        .then(() => {
          if (
            temporaryStorage &&
            'cmsSubscribe' in temporaryStorage &&
            user?.emailVerified
          ) {
            if (typeof temporaryStorage.cmsSubscribe.redirectUrl !== 'string') {
              setIsSubscribed(true);
              return;
            }

            if (isUrlWithProtocol(temporaryStorage.cmsSubscribe.redirectUrl)) {
              window.location.href = temporaryStorage.cmsSubscribe.redirectUrl;
              return;
            }

            navigate(
              temporaryStorage.cmsSubscribe.redirectUrl.startsWith('/')
                ? temporaryStorage.cmsSubscribe.redirectUrl
                : `/${temporaryStorage.cmsSubscribe.redirectUrl}`,
            );
            return;
          }

          if (typeof redirectUrl === 'string') {
            if (isUrlWithProtocol(redirectUrl)) {
              window.location.href = redirectUrl;
              return;
            }

            navigate(
              redirectUrl.startsWith('/') ? redirectUrl : `/${redirectUrl}`,
            );
          } else {
            setIsSubscribed(true);
          }
        })
        .catch((e) => {
          trackError(
            // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
            new RichError('Error subscribing in CMS block', JSON.stringify(e)),
          );
        });
    } else {
      showEmailSignupModal({ blockId, index, redirectUrl });
    }
  }, [i18n, user, blockId, index, temporaryStorage, redirectUrl, navigate]);

  useEffect(() => {
    if (
      temporaryStorage &&
      'cmsSubscribe' in temporaryStorage &&
      temporaryStorage.cmsSubscribe.blockId === blockId &&
      temporaryStorage.cmsSubscribe.index === index &&
      user?.emailVerified
    ) {
      subscribe();
    }
  }, [user?.emailVerified, blockId, index, subscribe, temporaryStorage]);

  return { subscribe, isSubscribed };
}
